import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { 
  BuildingIcon, 
  UsersIcon, 
  Banknote as BanknoteIcon, 
  ClockIcon,
  TrendingUpIcon,
  TrendingDownIcon,
  AlertTriangleIcon,
  Trash2Icon,
  XIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { useUltraAdminContext } from '../UltraAdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UltraDashboard = () => {
  const [stats, setStats] = useState({
    totalTenants: 0,
    activeTenants: 0,
    totalUsers: 0,
    monthlyRevenue: 0,
    pendingPayments: 0,
    trialAccounts: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [cleanupModal, setCleanupModal] = useState({ show: false, loading: false, confirmText: '' });
  const context = useUltraAdminContext();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (context?.refreshTrigger) {
      fetchDashboardData();
    }
  }, [context?.refreshTrigger]);

  const handleCleanup = async () => {
    try {
      setCleanupModal((prev) => ({ ...prev, loading: true }));
      const token = localStorage.getItem('ultra_admin_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/cleanup/tenants-and-users`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = response.data.deleted || {};
      toast.success(`Temizlik tamam: ${d.users || 0} kullanıcı, ${d.companies || 0} kiracı silindi`);
      setCleanupModal({ show: false, loading: false, confirmText: '' });
      fetchDashboardData();
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error(error.response?.data?.detail || 'Temizlik başarısız');
      setCleanupModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ultra_admin_token');
      if (!token) {
        toast.error('Ultra Admin token bulunamadı');
        setLoading(false);
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const statsResponse = await axios.get(`${BACKEND_URL}/api/ultra-admin/stats`, config);
      setStats(statsResponse.data);
      
      console.log('✅ Ultra Admin Stats loaded:', statsResponse.data);

    } catch (error) {
      console.error('Dashboard fetch error:', error);
      
      // No fallback data - show error
      setStats({
        totalTenants: 0,
        activeTenants: 0, 
        totalUsers: 0,
        monthlyRevenue: 0,
        pendingPayments: 0,
        trialAccounts: 0
      });
      
      toast.error('Ultra Admin istatistikleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Test Data Cleanup — Danger Zone */}
      <Card className="p-4 border-2 border-dashed border-red-300 bg-red-50/50">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Trash2Icon className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-red-900">Test Verisi Temizleme</h3>
              <p className="text-xs text-red-700 mt-0.5">
                <strong>Sadece</strong> tüm kullanıcı ve kiracıları siler. İşlemler, kartlar, ayarlar korunur. Ultra Admin girişi etkilenmez.
              </p>
            </div>
          </div>
          <Button
            onClick={() => setCleanupModal({ show: true, loading: false, confirmText: '' })}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="dashboard-cleanup-btn"
          >
            <Trash2Icon className="w-4 h-4 mr-2" />
            Test Verisi Sil
          </Button>
        </div>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Toplam Kiracı</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalTenants}</p>
              <p className="text-sm text-green-600 flex items-center mt-1">
                <TrendingUpIcon className="w-4 h-4 mr-1" />
                {stats.activeTenants} aktif
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <BuildingIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-green-500 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Toplam Kullanıcı</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalUsers}</p>
              <p className="text-sm text-blue-600 flex items-center mt-1">
                <UsersIcon className="w-4 h-4 mr-1" />
                Tüm kiracılar
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <UsersIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-purple-500 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Aylık Gelir</p>
              <p className="text-3xl font-bold text-gray-900">₺{stats.monthlyRevenue.toLocaleString('tr-TR')}</p>
              <p className="text-sm text-purple-600 flex items-center mt-1">
                <TrendingUpIcon className="w-4 h-4 mr-1" />
                Bu ay
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <BanknoteIcon className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-amber-500 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Bekleyen İşlem</p>
              <p className="text-3xl font-bold text-gray-900">{stats.pendingPayments}</p>
              <p className="text-sm text-amber-600 flex items-center mt-1">
                <ClockIcon className="w-4 h-4 mr-1" />
                {stats.trialAccounts} deneme
              </p>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <AlertTriangleIcon className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sistem Durumu</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <span className="text-sm font-medium">API Servisleri</span>
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Aktif</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <span className="text-sm font-medium">Veritabanı</span>
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Aktif</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <span className="text-sm font-medium">Ödeme Sistemi</span>
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">Mockup</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Cleanup Confirmation Modal */}
      {cleanupModal.show && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-red-700 flex items-center gap-2">
                <AlertTriangleIcon className="w-5 h-5" /> Test Verisi Sil
              </h3>
              <button
                onClick={() => setCleanupModal({ show: false, loading: false, confirmText: '' })}
                className="text-gray-400 hover:text-gray-600"
                disabled={cleanupModal.loading}
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                <p className="font-semibold">Bu işlem geri alınamaz.</p>
                <p className="mt-1">
                  Tüm <strong>kullanıcılar</strong> ve <strong>kiracı (şirket)</strong> kayıtları silinecek.
                  Diğer tüm veriler (işlemler, kartlar, ayarlar, kategoriler) olduğu gibi kalacak.
                </p>
                <p className="mt-2 text-xs">
                  ✓ Ultra Admin hesabınız korunur — giriş yapabilirsiniz.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Onaylamak için <code className="bg-gray-100 px-1 rounded">SIL</code> yazın:
                </label>
                <input
                  type="text"
                  value={cleanupModal.confirmText}
                  onChange={(e) =>
                    setCleanupModal((prev) => ({ ...prev, confirmText: e.target.value }))
                  }
                  placeholder="SIL"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 uppercase"
                  disabled={cleanupModal.loading}
                  data-testid="cleanup-confirm-input"
                  autoFocus
                />
              </div>
            </div>

            <div className="border-t bg-gray-50 px-6 py-3 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setCleanupModal({ show: false, loading: false, confirmText: '' })}
                disabled={cleanupModal.loading}
                data-testid="cleanup-cancel-btn"
              >
                İptal
              </Button>
              <Button
                onClick={handleCleanup}
                disabled={cleanupModal.confirmText.trim().toUpperCase() !== 'SIL' || cleanupModal.loading}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                data-testid="cleanup-execute-btn"
              >
                {cleanupModal.loading ? 'Siliniyor…' : (<><Trash2Icon className="w-4 h-4 mr-1" /> Kalıcı Olarak Sil</>)}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltraDashboard;